/**
 * The ledgers on this device. Every ledger is an org — with an id (o_ and 32
 * hex digits, the server's shape), a name, and a kind — whether it is in the
 * cloud or here. The cloud's orgs are the account's; the ones here are kept in
 * this registry, beside their files, and never touch the server: no members,
 * no roles. A ledger keeps its id when it moves between here and the cloud,
 * and with it, encrypted, its keyring (stores/sealed.js): the same passphrase
 * and recovery key wherever it is.
 *
 *   localStorage `okane.ledgers`  [{ id, name, kind: 'plain' | 'encrypted', file?, legacy? }]
 *   localStorage `okane.ledger`   the id of the one local mode opens
 *
 * Where each one's bytes are:
 *   plain       the file `ledger-<id>.sqlite` (OPFS; IndexedDB without it) —
 *               except the first, which keeps `okane.sqlite`, the name every
 *               device had before (`file`), so nothing moved to make it an org
 *   encrypted   IndexedDB `ledger-<id>.sealed` and `ledger-<id>.keys`; the
 *               first device's from before (`legacy`) keeps `okane.sealed`
 */
const LIST = 'okane.ledgers'
const CURRENT = 'okane.ledger'
const LEGACY_FILE = 'okane.sqlite'

const read = (key, fallback) => {
  try {
    return JSON.parse(localStorage.getItem(key) ?? 'null') ?? fallback
  } catch {
    return fallback
  }
}
const write = (key, value) => {
  try {
    if (value == null) localStorage.removeItem(key)
    else localStorage.setItem(key, JSON.stringify(value))
  } catch {
    // Private windows: the registry lasts as long as the page.
  }
}

/** A new org id, as the server would make it: a ledger made here can move up under it. */
export function newOrgId() {
  const bytes = crypto.getRandomValues(new Uint8Array(16))
  return `o_${[...bytes].map((b) => b.toString(16).padStart(2, '0')).join('')}`
}

export const ledgers = () => read(LIST, [])
const save = (list) => write(LIST, list)

/**
 * The registry, made the first time: this device's ledger from before becomes
 * its first org, its files where they were — readable in `okane.sqlite`, or
 * encrypted (`okane.sealed` = '1') in the sealed blob.
 */
export function ensureLedgers() {
  const list = ledgers()
  if (list.length) return list
  let sealed = false
  try {
    sealed = localStorage.getItem('okane.sealed') === '1'
    localStorage.removeItem('okane.sealed')
  } catch {
    // nothing kept
  }
  const first = sealed ? { id: newOrgId(), name: '', kind: 'encrypted', legacy: true } : { id: newOrgId(), name: '', kind: 'plain', file: LEGACY_FILE }
  save([first])
  write(CURRENT, first.id)
  return [first]
}

/** The local ledger local mode opens — the one chosen, else the first. */
export function currentLedger() {
  const list = ensureLedgers()
  const id = read(CURRENT, null)
  return list.find((l) => l.id === id) ?? list[0] ?? null
}
export const setCurrentLedger = (id) => write(CURRENT, id)
export const ledgerById = (id) => ledgers().find((l) => l.id === id) ?? null

export function addLedger(ledger) {
  save([...ledgers().filter((l) => l.id !== ledger.id), ledger])
  return ledger
}
export function updateLedger(id, patch) {
  save(ledgers().map((l) => (l.id === id ? { ...l, ...patch } : l)))
}
export function removeLedger(id) {
  save(ledgers().filter((l) => l.id !== id))
  if (read(CURRENT, null) === id) write(CURRENT, ledgers()[0]?.id ?? null)
}

/** A plain ledger's file name. */
export const fileOf = (ledger) => ledger.file ?? `ledger-${ledger.id}.sqlite`
/** The `file` to open it with: none for the first device's, so its storage steps still run. */
export const driverFile = (ledger) => (fileOf(ledger) === LEGACY_FILE ? undefined : fileOf(ledger))
export const isLegacyFile = (file) => file === LEGACY_FILE
/** Where an encrypted ledger's blob and keys are kept. */
export const sealedNames = (ledger) =>
  ledger.legacy ? { blob: 'okane.sealed', keys: 'okane.sealed.keys' } : { blob: `ledger-${ledger.id}.sealed`, keys: `ledger-${ledger.id}.keys` }
